import { type WistiaPlayerElement, type WistiaPlayerProps } from '@wistia/wistia-player-react';
import { useEffect, useRef, useState } from 'react';

type ApiReadyHandler = NonNullable<WistiaPlayerProps['onApiReady']>;

/**
 * Manages the play/preload lifecycle of the full video and its preview loop.
 *
 * The main video is mounted ahead of the play click (on hover/touch intent) so it's
 * ready to play instantly
 */
export const useFullBleedVideo = () => {
  /** Whether the user has started playback of the full video. */
  const [isPlaying, setIsPlaying] = useState(false);
  /** Whether the full video player has been mounted (may be preloaded ahead of `isPlaying`). */
  const [shouldMountPlayer, setShouldMountPlayer] = useState(false);
  /** Whether the preview loop has initialized and is safe to reveal. */
  const [isPreviewReady, setIsPreviewReady] = useState(false);
  /** Guards against firing the GTM play event more than once per mount. */
  const hasTrackedPlay = useRef(false);
  /** The full video's player instance, set once Wistia reports it's actually ready. */
  const [player, setPlayer] = useState<WistiaPlayerElement | null>(null);

  /** Mounts the full video player early, without starting playback. Call on hover/touch intent. */
  const preloadPlayer = () => setShouldMountPlayer(true);

  /** Mounts the full video player (if not already) and marks it as playing. */
  const handlePlay = () => {
    setShouldMountPlayer(true);
    setIsPlaying(true);
  };

  /** Captures the player instance from Wistia's own `api-ready` event. */
  const handlePlayerApiReady: ApiReadyHandler = (event) => setPlayer(event.target);

  /*
   * Wistia dispatches `api-ready` once the player is fully initialized and safe to
   * call `.play()` on.
   */
  useEffect(() => {
    if (isPlaying) player?.play();
  }, [isPlaying, player]);

  return {
    isPlaying,
    shouldMountPlayer,
    isPreviewReady,
    setIsPreviewReady,
    hasTrackedPlay,
    handlePlayerApiReady,
    preloadPlayer,
    handlePlay,
  };
};
